import React from 'react';
import { motion } from 'framer-motion';
import { Smartphone, ShoppingCart, CreditCard, RefreshCw, PlayCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ProcessStep {
  icon: React.ElementType;
  title: string;
  description: string;
  color: string;
}

// steps array moved inside component

const ProcessSection: React.FC = () => {
  const { t } = useTranslation();

  const steps: ProcessStep[] = [
    {
      icon: Smartphone,
      title: t('process.steps.scan.title'),
      description: t('process.steps.scan.description'),
      color: "bg-gradient-to-br from-orange-500 to-red-600"
    },
    {
      icon: ShoppingCart,
      title: t('process.steps.order.title'),
      description: t('process.steps.order.description'),
      color: "bg-gradient-to-br from-orange-400 to-red-500"
    },
    {
      icon: CreditCard,
      title: t('process.steps.pay.title'),
      description: t('process.steps.pay.description'),
      color: "bg-gradient-to-br from-orange-600 to-red-700"
    },
    {
      icon: RefreshCw,
      title: t('process.steps.repeat.title'),
      description: t('process.steps.repeat.description'),
      color: "bg-gradient-to-br from-red-500 to-orange-600"
    }
  ];
  return (
    <section className="mt-12 py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {t('process.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('process.description')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="relative"
            >
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-12 left-full w-full h-0.5 bg-gray-200 -z-10">
                  <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-0 h-0 border-t-4 border-t-transparent border-l-4 border-l-gray-200 border-b-4 border-b-transparent"></div>
                </div>
              )}
              
              <div className="text-center group">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  className={`w-24 h-24 ${step.color} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}
                >
                  <step.icon className="w-10 h-10 text-white" />
                </motion.div>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {step.title}
                </h3>
                
                <p className="text-gray-600">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-16 bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-8"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t('process.demo.title')}
              </h3>
              <p className="text-gray-600 mb-6">
                {t('process.demo.description')}
              </p>
              <button className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors flex items-center">
                <PlayCircle className="mr-2 h-5 w-5" />
                {t('process.demo.button')}
              </button>
            </div>
            <div className="relative">
              <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=450&fit=crop"
                  alt="Demo do TAPEAT"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg cursor-pointer hover:scale-110 transition-transform">
                    <PlayCircle className="w-10 h-10 text-orange-600 ml-1" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessSection;